import FeedbackRenderer from 'components/Feedback/FeedbackRenderer';
import Header from 'components/Feedback/Header';
import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FileViewer from 'components/Grading/FileViewer';
import { PageContext } from 'pages/StudentViewAssignmentFeedback/PageProvider';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Container, PDFViewer, RichTextArea, SheetViewer, Stack, styled, Text } from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Tablet = ({
    dispatch,
    currentFeedback,
    highlights,
    isGenerating,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
}) => {
    const params = useParams();
    const { t } = useTranslation('assignment');

    const {
        selectedFeedbackId,
        setSelectedFeedbackId,
        selectedInlineCommentId,
        setSelectedInlineCommentId,
        onClickThumbsUp,
        onClickThumbsDown,
    } = useContext(PageContext);

    const url = useMemo(() => getS3FileURL(currentFeedback.submission), [currentFeedback?.submission]);

    useEffect(() => {
        dispatch({ type: 'SET_SELECTED_FEEDBACK_ID_TO_FIRST', payload: params.feedbackId });
    }, [dispatch, params.feedbackId]);

    return (
        <CustomContainer>
            <Header />
            <ContentStack>
                <TopStack
                    isDocumentFullScreen={isDocumentFullScreen}
                    showBanner={Boolean(selectedFeedbackId || selectedInlineCommentId)}
                >
                    {isDocumentFullScreen && (selectedFeedbackId || selectedInlineCommentId) && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {selectedInlineCommentId
                                    ? t(
                                          'grading-section.document-preview.inline-comments-annotations',
                                          'Currently viewing annotations for inline comments.'
                                      )
                                    : t(
                                          'grading-section.document-preview.feedback-annotations',
                                          'Currently viewing annotations for feedback.'
                                      )}
                            </Text>
                        </BannerBox>
                    )}
                    {typeof currentFeedback.submission === 'string' ? (
                        <Stack sx={{ height: '100%', p: 3 }}>
                            <CustomRichTextArea readOnly value={currentFeedback.submission} onChange={() => {}} />
                        </Stack>
                    ) : currentFeedback.submission.type === 'application/pdf' ? (
                        <PDFViewer
                            url={url}
                            highlights={highlights}
                            inlineComments={currentFeedback.inline_comments}
                            selectedInlineCommentId={selectedInlineCommentId}
                            setSelectedInlineCommentId={setSelectedInlineCommentId}
                            setInlineComments={undefined}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                            isStudent
                        />
                    ) : [
                          'application/vnd.ms-excel',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'text/csv',
                      ].includes(currentFeedback.submission.type) ? (
                        <SheetViewer highlights={highlights} url={getS3FileURL(currentFeedback.submission)} />
                    ) : (
                        <FileViewer
                            device={'tablet'}
                            docUrl={getS3FileURL(currentFeedback.submission)}
                            fileType={currentFeedback.submission.type}
                        />
                    )}
                </TopStack>
                <BottomStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isGenerating ? (
                        <FeedbackRegenerating />
                    ) : (
                        <Stack sx={{ flex: 1 }}>
                            <FeedbackRenderer
                                feedback={currentFeedback}
                                selectedFeedbackId={selectedFeedbackId}
                                setSelectedFeedbackId={setSelectedFeedbackId}
                                onClickThumbsUp={onClickThumbsUp}
                                onClickThumbsDown={onClickThumbsDown}
                            />
                        </Stack>
                    )}
                </BottomStack>
            </ContentStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '65px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 65px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen, showBanner }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? `calc(100% - ${showBanner ? '24px' : '0px'})` : '48%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        height: '100%',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: isDocumentFullScreen ? 0 : '52%',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'height 150ms',
    };
});

export default Tablet;
